import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { Cross2Icon } from "@radix-ui/react-icons"
import { cva, type VariantProps } from "class-variance-authority"

import cn from "@/v2-ui/utils/utils.cn"
import { usePrimitiveText } from "@/v2-ui/text/offline"

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = (
  props: React.ComponentProps<typeof SheetPrimitive.Overlay>,
) => <SheetPrimitive.Overlay {...props} />
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
)

export type SheetContentText = {
  close?: string
}

type SheetContentProps = React.ComponentProps<typeof SheetPrimitive.Content> &
  VariantProps<typeof sheetVariants> & {
    renderDefaultCloseButton?: boolean
    text?: SheetContentText
  }

const SheetContent = ({
  side = "right",
  className,
  children,
  renderDefaultCloseButton = true,
  text,
  ...props
}: SheetContentProps) => {
  const { getText } = usePrimitiveText()
  const closeLabel = text?.close ?? getText("UI_Primitive_Sheet_Close").Name
  return (
    <SheetPrimitive.Content
      className={cn(sheetVariants({ side }), className)}
      {...props}
    >
      {renderDefaultCloseButton ? (
        <SheetPrimitive.Close
          aria-label={closeLabel}
          data-feature-code="UI_Primitive_Sheet_Close"
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary"
        >
          <Cross2Icon className="h-4 w-4" />
        </SheetPrimitive.Close>
      ) : null}
      {children}
    </SheetPrimitive.Content>
  )
}
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={className} {...props} />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={className} {...props} />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = (
  props: React.ComponentProps<typeof SheetPrimitive.Title>,
) => <SheetPrimitive.Title {...props} />
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = (
  props: React.ComponentProps<typeof SheetPrimitive.Description>,
) => <SheetPrimitive.Description {...props} />
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
